import { Navigate, useOutletContext } from 'react-router-dom'

import { Box, Spinner } from '@factorialco/deprecated-design-system'

import AuthorizeUser from 'modules/core/components/AuthorizeUser'
import { useNavigation } from 'modules/core/hooks/useNavigation'

import { usePayrollPaywallingGuard } from 'modules/payroll_bundle/hooks/usePayrollPaywallGuard'
import GuardedIncidencesPage from 'modules/payroll_bundle/pages/GuardedIncidencesPage'

interface CompensationsOutletContext {
  compensationsEnabled: boolean
}

/**
 * NavigateToEmployeeUpdatesPage handles routing logic for the employee updates page.
 *
 * Due to the limitations in the current inbox module, whereby we cannot conditionally
 * indicate the module where the user should go see the incidences:
 * - If compensations is enabled, show the GuardedIncidencesPage
 * - If compensations is not enabled but payroll is available, redirect to payroll incidences
 *
 * See: https://github.com/factorialco/factorial/pull/75312#discussion_r2319688874
 */
const NavigateToEmployeeUpdatesPage = () => {
  const { navigation } = useNavigation()
  const { compensationsEnabled } = useOutletContext<CompensationsOutletContext>()
  const { loadingFeatures, isPayrollAvailable } = usePayrollPaywallingGuard()

  const shouldRedirectToPayroll = !loadingFeatures && !compensationsEnabled && isPayrollAvailable

  if (loadingFeatures) {
    return (
      <Box
        gap="s8"
        padding="s48"
        alignItems="center"
        data-testid="navigate-employee-updates-loading"
      >
        <Spinner size="s40" />
      </Box>
    )
  }

  if (shouldRedirectToPayroll) {
    return <Navigate replace to={navigation.payrollBundle.incidences.fullPath()} />
  }

  return (
    <AuthorizeUser authorizeFn={() => compensationsEnabled}>
      <GuardedIncidencesPage />
    </AuthorizeUser>
  )
}

export default NavigateToEmployeeUpdatesPage
